import React, { Fragment, useLayoutEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { Grid, Row } from '@lux/components';
import { Caption, SubtitleSecondary, BodySecondary, HR } from '../Base';
import { noop, remCalc } from '@lux/helpers';

import createDescription from '../../helpers/create-description';
import formatPrice from '../../helpers/formatPrice';
import slugify from '../../helpers/slugify';

import IconArrowUp from '@lux/components/src/assets/svgs/arrow-up.svg';
import IconArrowDown from '@lux/components/src/assets/svgs/arrow-down.svg';
import IconFilter from '../../assets/svgs/filter.svg';

const DropdownList = styled.div`
  ${p => p.theme.media.md`
    max-width: ${p.theme.spacing(50)};
  `};
  max-height: ${remCalc(380)};
  overflow-y: auto;
  background-color: ${p => p.theme.colours.white};
  border: 1px solid ${p => p.theme.colours.zircon};
  box-shadow: 0 2px 5px 0 ${p => p.theme.colours.zircon};
  position: absolute;
  margin-top: ${p => (p.summary ? `-${p.theme.spacing(2.5)}` : 0)};
  left: ${p => p.theme.spacing(2)};
  right: ${p => p.theme.spacing(2)};
  z-index: 99;
  ${p => p.theme.media.md`
    height: auto;
    left: auto;
    right: auto;
  `};

  ${p =>
    p.show
      ? css`
          visibility: visible;
          opacity: 1;
        `
      : css`
          visibility: hidden;
          opacity: 0;
        `};

  transition: all 250ms ease;
`;

const DropdownItem = styled.div`
  padding: ${p => p.theme.spacing(2)};
  ${p =>
    p.selected &&
    css`
      background-color: ${p.theme.colours.powderBlue};
    `};
  cursor: pointer;
`;

const TextContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing(0)};
  ${p => p.theme.media.md`
    margin-bottom: ${p.theme.spacing(0.5)};
  `};
`;

const StyledFilterButton = styled(BodySecondary)`
  ${p => p.theme.media.md`
    display: none;
  `};
  cursor: pointer;
`;

const StyledIconFilter = styled(props => <IconFilter {...props} />)`
  vertical-align: middle;
  margin-left: ${p => p.theme.spacing(0.5)};
`;

const StyledAnchor = styled(SubtitleSecondary)`
  color: ${p => p.theme.colours.link};
`;

const StyledLabelText = styled(BodySecondary)`
  display: inline-block;
  color: ${p => p.theme.colours.grey_600};
  margin-right: ${p => p.theme.spacing(1)};
`;

const StyledSummary = styled(BodySecondary)`
  color: ${p => p.theme.colours.grey_600};
`;

const Ribbon = styled(Caption)`
  color: ${p => p.theme.colours.primary};
  cursor: pointer;
`;

const Title = styled(SubtitleSecondary)`
  margin-right: auto;
`;

const Price = styled(SubtitleSecondary)`
  margin-left: auto;
`;

const ClickableRegion = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`;

const ArrowIcon = styled.div`
  vertical-align: middle;
  margin: 0 ${p => p.theme.spacing(1)};
`;

const StyledHR = styled(HR)`
  margin: 0 ${p => p.theme.spacing(2)};
`;

const PlanSelect = props => {
  const {
    defaultPlanId,
    items,
    selectedItem,
    selectLabel,
    showFilters,
    summary,
    onFilterButtonClick,
    onSelectChange
  } = props;

  const [showList, toggleShowList] = useState(false);

  /**
   * Render the Horizontal Rule only if item is not selected
   * Removes the border top and bottom of the selected item
   *
   * @param {number} key
   * @returns {boolean}
   */
  const ConditionalHR = key => {
    const selectedIndex = items.findIndex(
      item => item.value === selectedItem.value
    );

    const excludeRender = !(
      key === selectedIndex ||
      key === selectedIndex - 1 ||
      key === items.length - 1
    );

    return excludeRender && <StyledHR />;
  };

  /**
   * Hide the item selection list and set the selected item
   *
   * @param {Object} item
   */
  const handleOnClick = item => {
    toggleShowList(false);
    onSelectChange(item);
  };

  const dropdownList = useRef(null);

  useLayoutEffect(() => {
    /**
     * Click function to close the dropdown list when clicked on anywhere outside of itself
     *
     * @param {Event} e
     */
    const handleOutsideClick = e => {
      const isOutside = !dropdownList.current.contains(e.target);

      if (showList && isOutside) {
        toggleShowList(false);
      }
    };

    document.addEventListener('click', handleOutsideClick);

    return () => document.removeEventListener('click', handleOutsideClick);
  }, [showList]);

  return (
    <div data-testid="dropdown">
      <TextContainer>
        <span>
          {selectLabel && <StyledLabelText>{selectLabel}</StyledLabelText>}
          <ClickableRegion
            onClick={() => toggleShowList(!showList)}
            data-testid="clickable"
          >
            <StyledAnchor>{selectedItem.text}</StyledAnchor>

            <ArrowIcon>
              {showList ? (
                <IconArrowUp width={8} height={8} />
              ) : (
                <IconArrowDown width={8} height={8} />
              )}
            </ArrowIcon>
          </ClickableRegion>
        </span>
        {showFilters && (
          <StyledFilterButton onClick={e => onFilterButtonClick(e)}>
            Filter <StyledIconFilter width={20} height={20} />
          </StyledFilterButton>
        )}
      </TextContainer>
      {summary && (
        <StyledSummary data-testid="summary">{summary}</StyledSummary>
      )}
      <DropdownList
        show={showList}
        summary={summary}
        ref={dropdownList}
        data-testid="dropdown-list"
      >
        {items &&
          items.map((item, key) => (
            <Fragment key={item.value}>
              <DropdownItem
                selected={item.value === selectedItem.value}
                onClick={() => handleOnClick(item)}
                data-testid={`dropdown-item-${slugify(selectedItem.text)}`}
              >
                <Grid>
                  <Row>
                    {defaultPlanId === item.value && (
                      <Ribbon data-testid="most-popular">Most Popular</Ribbon>
                    )}
                  </Row>
                  <Row middle="sm">
                    <Title data-testid="title">{item.text}</Title>
                    {item.monthlyCharges && (
                      <Price data-testid="price">
                        {`${formatPrice(item.monthlyCharges)}/mth`}
                      </Price>
                    )}
                  </Row>
                  <Row>
                    <BodySecondary data-testid="description">
                      {createDescription(item)}
                    </BodySecondary>
                  </Row>
                </Grid>
              </DropdownItem>
              {ConditionalHR(key)}
            </Fragment>
          ))}
      </DropdownList>
    </div>
  );
};

PlanSelect.defaultProps = {
  defaultPlanId: '',
  selectedItem: '',
  showFilters: false,
  summary: '',
  onFilterButtonClick: noop,
  onSelectChange: noop
};

PlanSelect.propTypes = {
  /** Default plan id to show MOST POPULAR tag */
  defaultPlanId: PropTypes.string,
  /** Array of items */
  items: PropTypes.array,
  /** Selected Item */
  selectedItem: PropTypes.object,
  /** The text to display for the dropdown list */
  selectLabel: PropTypes.string,
  /** Set to show filters */
  showFilters: PropTypes.bool,
  /** Set the summary text below the dropdown selection */
  summary: PropTypes.string,
  /** Fire the click event on the filter button */
  onFilterButtonClick: PropTypes.func,
  /** Function when the plan selection is changed */
  onSelectChange: PropTypes.func
};

export default PlanSelect;
